import { useReadAccess } from 'src/app/AppAuth/useReadAccess'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import {
  AUDIT_RESULT,
  AuditReadinessTestRecord,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { IsGatekeeperEnabled, GatekeeperFlag } from 'src/context/Gatekeeper'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useColumnHideList } from 'src/context/ColumnHideListContext'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import {
  CONTROL_CHECK_RESULT_PILL_COLOR,
  ControlListItemConfig,
  ControlListItemConfigMap,
  ControlListItemConfigurations,
} from './ControlListItem.constants'

export const getComplianceFrameworksForControl = (
  complianceFrameworks: ComplianceFramework[],
  currentIdList: string[],
): ComplianceFramework[] =>
  complianceFrameworks.filter((framework: ComplianceFramework) =>
    currentIdList?.includes(framework.getId()),
  ) || []

export const getMismatch = (
  complianceFrameworkIdsList: string[],
  complianceFrameworksInUse: ComplianceFramework[],
  hasAudit: boolean,
): boolean => {
  if (!hasAudit || !complianceFrameworkIdsList.length) {
    return false
  }
  return !complianceFrameworksInUse.some((framework) =>
    complianceFrameworkIdsList.includes(framework.getModelId()),
  )
}

export const getControlsListItemConfig = (
  config?: ControlListItemConfig,
): ControlListItemConfigMap =>
  config
    ? ControlListItemConfigurations[config]
    : ControlListItemConfigurations[ControlListItemConfig.DEFAULT]

// useShowControlRiskColumn reports whether this list should carry its Risks column.
//
// The column and its filter surface risk names, which sit behind CAN_VIEW_RISKS - a
// capability held independently of the CAN_VIEW_CONTROLS that gets a user onto these
// lists. ListControls withholds the risk data from those users server-side, so without
// this the column would render an empty cell on every row.
export const useShowControlRiskColumn = (
  config?: ControlListItemConfig,
): boolean => {
  const canViewRisks = useReadAccess(CAPABILITY.CAN_VIEW_RISKS)
  return !!getControlsListItemConfig(config).showRiskColumn && canViewRisks
}

export interface ControlsColumnsVisibility {
  showDepartment: boolean
  showId: boolean
  showName: boolean
  // The control's record in the GRC platform it was imported from. Index page
  // only: the embedded grids' fixed layouts have no track for it.
  showSource: boolean
  showLabels: boolean
  showRisk: boolean
  showFrameworks: boolean
  showEvidence: boolean
  showOperatingEffectiveness: boolean
  showStatus: boolean
  showOwner: boolean
  showAssignees: boolean
  showMenu: boolean
  isConfigurable: boolean
}

export const useControlsColumnsVisibility = (
  config?: ControlListItemConfig,
): ControlsColumnsVisibility => {
  const { isColumnHidden } = useColumnHideList()
  const canViewAuditScans = useReadAccess(CAPABILITY.CAN_VIEW_AUDIT_SCANS)
  const baseConfig = getControlsListItemConfig(config)
  const showRiskBase = useShowControlRiskColumn(config)
  const showLabelsBase = IsGatekeeperEnabled(GatekeeperFlag.LABELS)

  // Only the Controls index passes DEFAULT explicitly; embedded grids (evidence,
  // receptors, policy detail) omit the config and must keep every column.
  const isConfigurable = config === ControlListItemConfig.DEFAULT

  return {
    showDepartment:
      Boolean(baseConfig.showDepartmentColumn) &&
      (!isConfigurable ||
        !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'department')),
    showId:
      Boolean(baseConfig.showModelIdColumn) &&
      (!isConfigurable ||
        !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'id')),
    showName: Boolean(baseConfig.showControlNameColumn),
    showSource:
      isConfigurable &&
      !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'source'),
    showLabels:
      showLabelsBase &&
      (!isConfigurable ||
        !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'labels')),
    showRisk:
      showRiskBase &&
      (!isConfigurable ||
        !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'risks')),
    showFrameworks:
      Boolean(baseConfig.showFrameworkColumn) &&
      (!isConfigurable ||
        !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'frameworks')),
    showEvidence:
      Boolean(baseConfig.showLinkedEvidenceColumn) &&
      (!isConfigurable ||
        !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'evidence')),
    showOperatingEffectiveness:
      canViewAuditScans &&
      Boolean(baseConfig.showOperatingEffectivenessColumn) &&
      (!isConfigurable ||
        !isColumnHidden(
          INDEX_PAGE_WITH_FILTERS.CONTROLS,
          'operatingEffectiveness',
        )),
    showStatus:
      Boolean(baseConfig.showStatusColumn) &&
      (!isConfigurable ||
        !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'status')),
    showOwner:
      Boolean(baseConfig.showOwnerColumn) &&
      (!isConfigurable ||
        !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'owner')),
    showAssignees:
      Boolean(baseConfig.showOwnerColumn) &&
      (!isConfigurable ||
        !isColumnHidden(INDEX_PAGE_WITH_FILTERS.CONTROLS, 'assignees')),
    showMenu: Boolean(baseConfig.showMenuColumn),
    isConfigurable,
  }
}

export const getControlsListItemTemplateColumns = (
  showContentMatch: boolean,
  canViewAuditScans: boolean,
  config?: ControlListItemConfig,
  showRiskColumn = false,
  columnsVisibility?: ControlsColumnsVisibility,
): string => {
  if (columnsVisibility?.isConfigurable) {
    const tracks: string[] = []
    if (showContentMatch) tracks.push('min-content')
    if (columnsVisibility.showDepartment) tracks.push('min-content')
    if (columnsVisibility.showId) tracks.push('fit-content(20ch)')
    if (columnsVisibility.showName) tracks.push('minmax(auto, 30%)')
    if (columnsVisibility.showSource) tracks.push('auto')
    if (columnsVisibility.showLabels) tracks.push('auto')
    if (columnsVisibility.showRisk) tracks.push('auto')
    if (columnsVisibility.showFrameworks) tracks.push('auto')
    if (columnsVisibility.showEvidence) tracks.push('auto')
    if (columnsVisibility.showOperatingEffectiveness) tracks.push('auto')
    if (columnsVisibility.showStatus) tracks.push('auto')
    if (columnsVisibility.showOwner) tracks.push('auto')
    if (columnsVisibility.showAssignees) tracks.push('auto')
    if (columnsVisibility.showMenu) tracks.push('auto')
    return tracks.join(' ')
  }
  const showLabels = IsGatekeeperEnabled(GatekeeperFlag.LABELS)
  // Only the configurations that render a Risks cell widen for it, and only when the
  // viewer may see risks, so the header, the rows and the track count stay in step.
  const withRisks = (t: string) => (showRiskColumn ? `${t} auto` : t)

  let template = ''
  switch (config) {
    case ControlListItemConfig.DEFAULT:
      if (canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(8, auto)',
        )
      } else if (canViewAuditScans && !showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else if (!canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)',
        )
      }
      return showContentMatch ? `min-content ${template}` : template

    case ControlListItemConfig.AI_DASHBOARD:
      if (canViewAuditScans && showLabels) {
        template = 'min-content fit-content(20ch) 1fr auto fit-content(200px)'
      } else if (canViewAuditScans && !showLabels) {
        template = 'min-content fit-content(20ch) 1fr fit-content(200px)'
      } else if (!canViewAuditScans && showLabels) {
        template = 'min-content fit-content(20ch) 1fr auto'
      } else {
        template = 'min-content fit-content(20ch) 1fr'
      }
      return showContentMatch ? `min-content ${template}` : template
    case ControlListItemConfig.EVIDENCE:
      if (canViewAuditScans && showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(8, auto)'
      } else if (canViewAuditScans && !showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)'
      } else if (!canViewAuditScans && showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)'
      } else {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)'
      }
      return showContentMatch ? `min-content ${template}` : template

    case ControlListItemConfig.FRAMEWORK_REQUIREMENT:
      if (canViewAuditScans && showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)'
      } else if (canViewAuditScans && !showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)'
      } else if (!canViewAuditScans && showLabels) {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)'
      } else {
        template =
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(5, auto)'
      }
      return showContentMatch ? `min-content ${template}` : template
    case ControlListItemConfig.CONTROLS_BY_REQUIREMENT:
      if (canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(8, auto)',
        )
      } else if (canViewAuditScans && !showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else if (!canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)',
        )
      }
      return showContentMatch ? `min-content ${template}` : template
    case ControlListItemConfig.EVIDENCE_VIA_REQUEST:
      if (canViewAuditScans && showLabels) {
        template = 'min-content min-content minmax(auto, 30%) repeat(8, auto)'
      } else if (canViewAuditScans && !showLabels) {
        template = 'min-content min-content minmax(auto, 30%) repeat(7, auto)'
      } else if (!canViewAuditScans && showLabels) {
        template = 'min-content min-content minmax(auto, 30%) repeat(7, auto)'
      } else {
        template = 'min-content min-content minmax(auto, 30%) repeat(6, auto)'
      }
      return showContentMatch ? `min-content ${template}` : template
    default:
      if (canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(8, auto)',
        )
      } else if (canViewAuditScans && !showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else if (!canViewAuditScans && showLabels) {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(7, auto)',
        )
      } else {
        template = withRisks(
          'min-content fit-content(20ch) minmax(auto, 30%) repeat(6, auto)',
        )
      }
      return showContentMatch ? `min-content ${template}` : template
  }
}

export const getControlCheckResultPillLabel = (
  result: AUDIT_RESULT,
  testName?: CONTROL_TEST_NAMES,
): string => {
  switch (result) {
    case AUDIT_RESULT.PASS:
      return 'Passed'
    case AUDIT_RESULT.FAIL:
      return 'Failed'
    case AUDIT_RESULT.NOT_APPLICABLE:
      return 'Not Applicable'
    case AUDIT_RESULT.LAMBDA_ERROR:
    case AUDIT_RESULT.UNKNOWN:
      return 'Could not be evaluated'
    case AUDIT_RESULT.MISSING_TEST_PROCEDURES:
      return 'Missing test procedures'
    case AUDIT_RESULT.MISSING_SUGGESTIONS:
      return 'Missing required evidence'
    case AUDIT_RESULT.NOT_RUN:
    default:
      if (testName === CONTROL_TEST_NAMES.POLICY_MATCH) {
        return 'Not Examined'
      }
      return 'Not Tested'
  }
}

// The text the Operating Effectiveness column shows for a control, from its latest
// control test results; used by search so a row is found by "Failed", "Outdated", ...
export const getControlEffectivenessText = (
  tests: AuditReadinessTestRecord[] | undefined,
  isNotApplicable: boolean,
): string => {
  const spotCheck = tests?.find(
    (test) => test.getTestName() === CONTROL_TEST_NAMES.SPOT_CHECK,
  )
  if (isNotApplicable || !spotCheck) {
    return getControlCheckResultPillLabel(
      isNotApplicable ? AUDIT_RESULT.NOT_APPLICABLE : AUDIT_RESULT.NOT_RUN,
      CONTROL_TEST_NAMES.SPOT_CHECK,
    )
  }
  const label = getControlCheckResultPillLabel(
    spotCheck.getResult(),
    CONTROL_TEST_NAMES.SPOT_CHECK,
  )
  return spotCheck.getIsStale() ? `${label} Outdated` : label
}

export const getControlCheckResultPillColor = (
  result: AUDIT_RESULT,
): string => {
  return CONTROL_CHECK_RESULT_PILL_COLOR[result]
}
